/*******************************************************************************
 * Copyright (c) 2023 GetChkd
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 2.0.0
 *
 *******************************************************************************/
import React from "react";
import { Drawer, Grid, Tooltip, IconButton, MenuItem } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BsFillHouseFill,
  BsFillPeopleFill,
  BsServer,
} from "react-icons/bs";
import {
  FaBuilding,
  FaNetworkWired,
  FaUserCog,
} from "react-icons/fa";
import { MdSettingsApplications } from "react-icons/md";
import { RiDeviceFill } from "react-icons/ri";

const drawerWidth = 80;

const menuItems = [
  { title: "Dashboard", path: "/dashboard", icon: <BsFillHouseFill /> },
  { title: "Manage Users", path: "/manage-users", icon: <BsFillPeopleFill /> },
  { title: "Manage Organizations", path: "/manage-organizations", icon: <FaBuilding /> },
  { title: "Manage Applications", path: "/manage-applications", icon: <MdSettingsApplications /> },
  { title: "Manage Roles", path: "/manage-roles", icon: <FaUserCog /> },
  { title: "Manage Identity Providers", path: "/manage-identity-provider", icon: <BsServer /> },
  { title: "Manage Devices", path: "/manage-devices", icon: <RiDeviceFill /> },
  { title: "Manage Access", path: "/manage-access", icon: <FaNetworkWired /> },
];
/**
 * MenuSideBar Component
 * 
 * This component renders a collapsible side navigation drawer for an admin panel.
 * It provides quick access to various sections such as Dashboard, Users, Organizations,
 * Applications, Roles, Identity Providers, Devices, and Networks.
 * 
 * Features:
 * - Uses Material UI's Drawer for the sidebar.
 * - Displays icons with tooltips for better user experience.
 * - Highlights the active menu item based on the current route.
 * - Becomes scrollable when content exceeds available height.
 * - Uses React Router's `useNavigate` and `useLocation` for navigation.
 * 
 * Dependencies:
 * - @mui/material (for UI components)
 * - react-icons (for menu icons)
 * - react-router-dom (for navigation)
 */
const MenuSideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getMenuItemStyle = (path) => ({
    background: location.pathname.startsWith(path) ? "rgb(195 243 222)" : "white",
  });

  return (
    <Drawer
      anchor="left"
      open
      variant="persistent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          border: "1px solid rgba(144, 202, 249, 0.46)",
          height: "calc(100% - 100px)",
          top: 100,
          position: "absolute",
          overflowY: "auto",  
          scrollbarWidth: "thin",
        },
      }}
    >
      <Grid container spacing={2} sx={{ paddingTop: "20px" }}>
        {menuItems.map(({ title, path, icon }) => (
          <Grid item xs={12} key={path}>
            <Tooltip title={title} placement="right" arrow>
              <MenuItem
                sx={{
                  padding: "20px!important",
                  "&:hover": { background: "#c3f3de" },
                  ...getMenuItemStyle(path),
                }}
                onClick={() => navigate(path)}
              >
                <IconButton color="primary">{icon}</IconButton>
              </MenuItem>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Drawer>
  );
};

export default MenuSideBar;
